import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSignaturePad } from './use-signature-pad';

type SignaturePadDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (file: File) => void;
  /** Whether a signature already exists: an empty pad then keeps it. */
  hasExistingSignature: boolean;
};

/**
 * Dialog holding the signature pad: draw, clear, validate. Full width on
 * mobile; the pad refits on screen rotation.
 *
 * @param props - See {@link SignaturePadDialogProps}.
 * @returns The dialog.
 */
export function SignaturePadDialog({
  open,
  onOpenChange,
  onSave,
  hasExistingSignature,
}: SignaturePadDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="max-w-full rounded-none p-4 sm:max-w-lg sm:rounded-lg sm:p-6">
        <DialogHeader>
          <DialogTitle>{t('signature-form.dialog.title')}</DialogTitle>
          <DialogDescription>
            {t('signature-form.dialog.description')}
          </DialogDescription>
        </DialogHeader>
        {/* Mounted only while open, so each opening starts on a blank pad. */}
        <SignaturePadDialogBody
          onOpenChange={onOpenChange}
          onSave={onSave}
          hasExistingSignature={hasExistingSignature}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Pad, error and actions of the signature dialog.
 *
 * @param props - The dialog's callbacks and whether a signature exists.
 * @returns The dialog body.
 */
function SignaturePadDialogBody({
  onOpenChange,
  onSave,
  hasExistingSignature,
}: Omit<SignaturePadDialogProps, 'open'>) {
  const { t } = useTranslation();
  const { setCanvasRef, clear, validate, error, isSaving } = useSignaturePad({
    hasExistingSignature,
    onSave,
    onOpenChange,
  });

  return (
    <>
      <div className="flex flex-col gap-2">
        <canvas
          ref={setCanvasRef}
          aria-label={t('signature-form.dialog.title')}
          className="h-48 max-h-[45dvh] w-full touch-none rounded-md border border-border bg-white sm:h-56"
        />
        {error && (
          <p
            role="alert"
            className="text-destructive text-sm"
          >
            {t(error)}
          </p>
        )}
      </div>
      <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
        <Button
          type="button"
          variant="ghost"
          onClick={clear}
        >
          {t('signature-form.btn.clear')}
        </Button>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t('btn.cancel')}
          </Button>
          <Button
            type="button"
            onClick={validate}
            loading={isSaving}
          >
            {t('signature-form.btn.validate')}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
